import { ArrowDropDown, Engineering } from "@mui/icons-material";
import { Divider, Menu, MenuItem } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../api/axios";
import ToolbarButton from "../../components/ui/toolbar/toolbarButton/toolbarButton.component";
import { USERS_ENDPOINT } from "../../consts";
import { formatUserName } from "../../utils";

type TechnicianOption = {
  id: number | string;
  first_name: string;
  last_name: string;
  email: string;
};

// The people the job form's "Assigned to" field offers: active members of a
// technician group. One page is plenty - a workspace has a handful of them.
const TECHNICIANS_PARAMS = { is_active: "true", service_role: "true", page_size: 100 };

interface TechnicianQuickFilterProps {
  /** The list's ?assigned_to= value ("" when not filtered by a person). */
  value: string;
  onChange: (userId: string | null) => void;
}

/**
 * A quick filter button that picks the technician the jobs list is filtered
 * by, from a menu. Drives ?assigned_to=, like "Assigned to me" does.
 */
export const TechnicianQuickFilter = ({ value, onChange }: TechnicianQuickFilterProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [technicians, setTechnicians] = useState<TechnicianOption[] | null>(null);
  const [lookedUpUser, setLookedUpUser] = useState<TechnicianOption | null>(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(USERS_ENDPOINT, { params: TECHNICIANS_PARAMS })
      .then(({ data }) => {
        if (!cancelled) {
          setTechnicians(data?.results ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTechnicians([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedTechnician = value
    ? technicians?.find((technician) => String(technician.id) === value)
    : undefined;

  // ?assigned_to= can name someone who isn't on the menu - "Assigned to me"
  // for a user outside the technician groups, or a link to a since-
  // deactivated technician - so they're looked up by id to show their name.
  const needsLookup = Boolean(value) && technicians !== null && !selectedTechnician;
  useEffect(() => {
    if (!needsLookup) {
      return undefined;
    }

    let cancelled = false;
    axios
      .get(USERS_ENDPOINT, { params: { id: value } })
      .then(({ data }) => {
        if (!cancelled) {
          setLookedUpUser(data?.results?.[0] ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLookedUpUser(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [needsLookup, value]);

  const selectedUser =
    selectedTechnician ??
    (lookedUpUser && String(lookedUpUser.id) === value ? lookedUpUser : null);

  const handleSelect = (userId: string | null) => {
    setAnchorEl(null);
    if ((userId ?? "") !== value) {
      onChange(userId);
    }
  };

  return (
    <>
      <ToolbarButton
        icon={<Engineering />}
        label={selectedUser ? formatUserName(selectedUser) : t("Technician")}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        color={value ? "var(--primaryColor)" : undefined}
        trailingAdornment={<ArrowDropDown fontSize="small" />}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { width: anchorEl?.clientWidth, maxHeight: 360 } } }}
      >
        <MenuItem dense selected={!value} onClick={() => handleSelect(null)}>
          {t("All technicians")}
        </MenuItem>
        <Divider />
        {technicians === null && (
          <MenuItem dense disabled>
            {t("Loading...")}
          </MenuItem>
        )}
        {technicians?.length === 0 && (
          <MenuItem dense disabled>
            {t("No options")}
          </MenuItem>
        )}
        {technicians?.map((technician) => (
          <MenuItem
            key={technician.id}
            dense
            selected={String(technician.id) === value}
            onClick={() => handleSelect(String(technician.id))}
          >
            {formatUserName(technician)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
